<template>
  <UiModal
    :model-value="modelValue"
    :title="t('support.newTitle')"
    size="lg"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <form id="support-form" class="stack gap-4" @submit.prevent="submit">
      <UiField :label="t('support.subject')" :hint="t('support.subjectHint')" required>
        <template #default="{ id }">
          <UiInput :id="id" v-model="form.subject" maxlength="160" required />
        </template>
      </UiField>

      <div class="grid-2">
        <UiField :label="t('support.category')">
          <template #default="{ id }">
            <UiSelect :id="id" v-model="form.category" :options="categoryOptions" />
          </template>
        </UiField>

        <UiField :label="t('support.priority')" :hint="t('support.priorityHint')">
          <template #default="{ id }">
            <UiSelect :id="id" v-model="form.priority" :options="priorityOptions" />
          </template>
        </UiField>
      </div>

      <UiField :label="t('support.description')" :hint="t('support.descriptionHint')" required>
        <template #default="{ id }">
          <UiTextarea :id="id" v-model="form.description" :rows="8" :maxlength="8000" required />
        </template>
      </UiField>

      <!-- ── Attachments ──────────────────────────────────────────────────────
           Uploaded as they are chosen rather than with the form: a screenshot
           that fails to upload should say so while there is still a form to fix
           it in, not after the ticket has been sent. -->
      <UiField :label="t('support.attachments')" :hint="t('support.attachHint', { max: maxAttachments })">
        <template #default="{ id }">
          <div class="stack gap-2">
            <input
              :id="id"
              ref="fileInput"
              type="file"
              multiple
              class="file"
              :disabled="uploading || form.attachments.length >= maxAttachments"
              @change="pick"
            >
            <ul v-if="form.attachments.length" class="files">
              <li v-for="file in form.attachments" :key="file.id" class="files__item">
                <span class="files__name">{{ file.filename }}</span>
                <span class="text-xs text-mute">{{ kb(file.size) }}</span>
                <UiClose size="sm" :label="t('support.removeAttachment')" @click="drop(file.id)" />
              </li>
            </ul>
          </div>
        </template>
      </UiField>

      <!-- Said out loud rather than collected silently. What we take is
           uncontroversial, and a person who knows it is being taken is a person
           who does not have to be asked for it later. -->
      <p class="notice text-xs">{{ t('support.contextNotice') }}</p>

      <p v-if="error" class="form-error" role="alert">{{ errorMessage(error, t) }}</p>
    </form>

    <template #footer>
      <UiButton variant="secondary" @click="$emit('update:modelValue', false)">
        {{ t('common.cancel') }}
      </UiButton>
      <UiButton type="submit" form="support-form" :loading="saving" :disabled="uploading">
        {{ t('support.send') }}
      </UiButton>
    </template>
  </UiModal>
</template>

<script setup>
import { reactive, ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import {
  UiModal, UiField, UiInput, UiTextarea, UiSelect, UiButton, UiClose,
  useToast, errorMessage,
} from '@general/ui-kit'

import { supportApi, browserContext } from '../api'

const props = defineProps({
  modelValue: Boolean,
  // From /support/meta, so adding a category is a backend change.
  meta: { type: Object, default: () => ({ categories: [], priorities: [], maxAttachments: 5 }) },
})
const emit = defineEmits(['update:modelValue', 'created'])

const { t, locale } = useI18n()
const toast = useToast()
const route = useRoute()

const blank = { subject: '', description: '', category: 'question', priority: 'normal', attachments: [] }
const form = reactive({ ...blank, attachments: [] })
const saving = ref(false)
const uploading = ref(false)
const error = ref(null)
const fileInput = ref(null)

const maxAttachments = computed(() => props.meta.maxAttachments ?? 5)

const categoryOptions = computed(() => (props.meta.categories ?? [])
  .map(value => ({ value, label: t(`support.categories.${value}`) })))

const priorityOptions = computed(() => (props.meta.priorities ?? [])
  .map(value => ({ value, label: t(`support.priorities.${value}`) })))

const kb = (bytes) => `${Math.max(1, Math.round((bytes ?? 0) / 1024))} kB`

// Refilled on open, so a cancelled draft never bleeds into the next one.
watch(() => props.modelValue, (open) => {
  if (!open) return
  error.value = null
  Object.assign(form, blank, { attachments: [] })
  if (fileInput.value) fileInput.value.value = ''
})

async function pick(event) {
  const chosen = [...(event.target.files ?? [])]
  if (!chosen.length) return

  const room = maxAttachments.value - form.attachments.length
  uploading.value = true
  try {
    for (const file of chosen.slice(0, room)) {
      const { file: stored } = await supportApi.upload(file)
      form.attachments.push(stored)
    }
    if (chosen.length > room) toast.error(t('support.tooManyAttachments', { max: maxAttachments.value }))
  } catch (err) {
    toast.error(errorMessage(err, t))
  } finally {
    uploading.value = false
    // Cleared so choosing the same file twice still fires a change event.
    if (fileInput.value) fileInput.value.value = ''
  }
}

function drop(id) {
  form.attachments = form.attachments.filter(file => file.id !== id)
}

async function submit() {
  saving.value = true
  error.value = null
  try {
    const { ticket } = await supportApi.open({
      subject: form.subject,
      description: form.description,
      category: form.category,
      priority: form.priority,
      attachments: form.attachments.map(file => file.id),
      // The route they were on when they pressed the button, not this modal's.
      context: browserContext({ route: route.fullPath, locale: locale.value }),
    })
    toast.success(t('support.openedToast', { reference: ticket.reference }))
    emit('created', ticket)
    emit('update:modelValue', false)
  } catch (err) {
    error.value = err
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}
@media (max-width: 520px) {
  .grid-2 { grid-template-columns: 1fr; }
}

.file {
  font-size: var(--text-sm);
  color: var(--color-text-soft);
}

.files {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.files__item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-2);
  border-radius: var(--radius-sm);
}
.files__name {
  flex: 1 1 auto;
  font-size: var(--text-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice {
  color: var(--color-text-mute);
  border-left: 2px solid var(--color-border);
  padding-left: var(--space-3);
}

.form-error {
  padding: var(--space-3);
  background: var(--color-danger-soft);
  color: var(--color-danger);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}
</style>
